
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X, XOctagon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Banner, BannerSeverity } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { paintChrome } from '@/shared/lib/browser-chrome';
import { useUiStore } from '@/shared/lib/uiStore';
import { useRevealStore } from '@/features/ai-assistant';
import { useBanners } from '../model/useBanners';
import styles from './BannerBar.module.css';

const ICONS: Record<BannerSeverity, React.ReactNode> = {
  error: <XOctagon size={16} aria-hidden />,
  warning: <AlertTriangle size={16} aria-hidden />,
  success: <CheckCircle2 size={16} aria-hidden />,
  info: <Info size={16} aria-hidden />,
};

const EXIT_MS = 200;

const LIVE: Record<BannerSeverity, 'assertive' | 'polite'> = {
  error: 'assertive',
  warning: 'polite',
  success: 'polite',
  info: 'polite',
};

export const BannerBar: React.FC = () => {
  const { banner, next, dismiss } = useBanners();
  const menuOpen = useUiStore((s) => s.mobileNavOpen);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [swapInId, setSwapInId] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const slotRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const aiOpen = useRevealStore((r) => r.enabled && r.state !== 'collapsed');
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = slotRef.current;
    if (!el) {
      root.style.removeProperty('--banner-h');
      return;
    }
    const measure = () =>
      root.style.setProperty('--banner-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [banner?.id]);
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty('--banner-h');
    },
    [],
  );

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    if (aiOpen) {
      paintChrome('banner', null);
      return;
    }
    const paint = () => paintChrome('banner', { top: getComputedStyle(bar).backgroundColor });
    paint();
    const mo = new MutationObserver(paint);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const scheme = window.matchMedia('(prefers-color-scheme: dark)');
    scheme.addEventListener('change', paint);
    return () => {
      mo.disconnect();
      scheme.removeEventListener('change', paint);
      paintChrome('banner', null);
    };
  }, [banner?.id, banner?.severity, aiOpen]);

  const startClose = useCallback(
    (b: Banner) => {
      if (timer.current !== undefined) return;
      if (next) {
        setSwapInId(next.id);
        dismiss(b);
        return;
      }
      setSwapInId(null);
      const instant = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setClosingId(b.id);
      timer.current = window.setTimeout(
        () => {
          timer.current = undefined;
          setClosingId(null);
          dismiss(b);
        },
        instant ? 0 : EXIT_MS,
      );
    },
    [dismiss, next],
  );

  if (!banner) return null;

  const link = banner.linkUrl ? (
    <a className={styles.link} href={banner.linkUrl} target="_blank" rel="noopener noreferrer">
      {banner.linkLabel || 'Подробнее'}
    </a>
  ) : banner.massIncidentId ? (
    <Link className={styles.link} to={`/tickets?mass=only`}>
      Заявки по сбою
    </Link>
  ) : null;

  const content = (
    <div className={styles.inner}>
      <span className={styles.icon}>{ICONS[banner.severity]}</span>
      <span className={styles.text}>{banner.text}</span>
      {link}
      <button
        type="button"
        className={styles.close}
        onClick={() => startClose(banner)}
        aria-label={banner.dismissible ? 'Скрыть сообщение' : 'Скрыть сообщение до следующего входа'}
        title={
          banner.dismissible
            ? 'Скрыть сообщение'
            : 'Сообщение важное: скроется до перезагрузки страницы'
        }
      >
        <X size={16} aria-hidden />
      </button>
    </div>
  );
  const tone = cn(
    styles[banner.severity],
    swapInId === banner.id && styles.slotSwap,
    closingId === banner.id && styles.slotClosing,
  );

  return (
    <>
      { }
      <div className={styles.edge} data-banner aria-hidden />
      { }
      <div key={banner.id} ref={slotRef} data-banner className={cn(styles.slot, tone)}>
        <div ref={barRef} className={styles.bar} role="status" aria-live={LIVE[banner.severity]}>
          {content}
        </div>
      </div>
      { }
      {createPortal(
        <div
          key={banner.id}
          className={cn(styles.slot, styles.slotMirror, menuOpen && styles.slotMirrorOpen, tone)}
        >
          <div className={styles.bar}>{content}</div>
        </div>,
        document.body,
      )}
    </>
  );
};
